import { createContext, useContext } from "react";

import type { Rating, SeasonRating } from "../types/rating";
import type { EntityId, FilmMetadata, Season } from "../types/media";
import type { WatchSeason, WatchSummary } from "../utility/watchHistory";
import type { calculateWatchStatistics } from "../utility/watchHistory";
import type { WatchEntry, FilmViewingOptions, SeasonViewingOptions, ViewingOptions, UpdateViewingOptions, DeleteViewingOptions, TotalViewingOptions, WatchMutationResult } from "./watchHistoryTypes";
export type WatchHistorySummary = WatchSummary;
type WatchHistoryAction<T> = (options: T) => Promise<WatchMutationResult | null>;

export interface WatchHistoryContextValue {
  userId: string | null | undefined;
  ratings: Rating[];
  summaries: WatchHistorySummary[];
  summariesByFilmId: Map<string, WatchHistorySummary>;
  statistics: ReturnType<typeof calculateWatchStatistics>;
  entriesByFilmId: Record<string, WatchEntry[]>;
  seasonSummaries: WatchHistorySummary[];
  seasonSummariesByScope: Map<string, WatchHistorySummary>;
  seasonEntriesByScope: Record<string, WatchEntry[]>;
  loading: boolean;
  error: Error | null;
  isOwner: boolean;
  getSummary: (filmId: string | number) => WatchHistorySummary | null;
  getSeasonSummary: (filmId: string | number, seasonOrKey: EntityId | WatchSeason) => WatchHistorySummary | null;
  loadEntries: (film: EntityId | FilmMetadata, rating?: Rating | null) => Promise<WatchEntry[]>;
  loadSeasonSummaries: (film: EntityId | FilmMetadata, rating?: Rating | null) => Promise<WatchSummary[]>;
  loadSeasonEntries: (film: FilmMetadata, season: Season, rating?: Rating | null, seasonRating?: SeasonRating | null) => Promise<WatchEntry[]>;
  ensureFirstViewing: WatchHistoryAction<FilmViewingOptions>;
  addViewing: WatchHistoryAction<FilmViewingOptions & ViewingOptions>;
  setTotalViewings: WatchHistoryAction<FilmViewingOptions & TotalViewingOptions>;
  updateViewing: WatchHistoryAction<FilmViewingOptions & UpdateViewingOptions>;
  deleteViewing: WatchHistoryAction<FilmViewingOptions & DeleteViewingOptions>;
  ensureSeasonFirstViewing: WatchHistoryAction<SeasonViewingOptions>;
  addSeasonViewing: WatchHistoryAction<SeasonViewingOptions & ViewingOptions>;
  setSeasonTotalViewings: WatchHistoryAction<SeasonViewingOptions & TotalViewingOptions>;
  updateSeasonViewing: WatchHistoryAction<SeasonViewingOptions & UpdateViewingOptions>;
  deleteSeasonViewing: WatchHistoryAction<SeasonViewingOptions & DeleteViewingOptions>;
}

export const WatchHistoryContext = createContext<WatchHistoryContextValue | null>(null);

export const useWatchHistory = () => {
  const context = useContext(WatchHistoryContext);
  if (!context) throw new Error("useWatchHistory must be used within WatchHistoryProvider");
  return context;
};

export const useOptionalWatchHistory = () => useContext(WatchHistoryContext);
